import type { ApiClient } from '@carbonwebmail/api-client'
import type { DuplicateGroupDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * Contacts that reach the same mailbox, and clearing them.
 *
 * Clearing deletes contacts, so this holds a selection and a count and decides
 * nothing: the server finds the groups and the server picks the survivor — the
 * oldest row, which is the one everything else has had longest to point at.
 *
 * **What travels is the mailboxes, not the contact ids.** The rows behind a
 * group are read by the server in the same transaction that deletes them, so a
 * dialog left open while somebody else imports a file clears what is there
 * rather than what was drawn.
 *
 * The list reloads after a clear rather than dropping the groups locally. A
 * group another request cleared first is already gone, and guessing here would
 * take a group off the screen that is still there.
 */

export type DuplicatesState = AsyncState<readonly DuplicateGroupDto[]>

export function useDuplicates({
  client,
  onCleared,
}: {
  readonly client: ApiClient
  /** Told only when rows actually went, so the board behind reloads once. */
  readonly onCleared: () => void
}) {
  const load = useCallback(async () => (await client.duplicates()).groups, [client])
  const { state, reload } = useLoaded<readonly DuplicateGroupDto[]>(
    load,
    'We could not look for duplicates. Please try again in a moment.',
  )

  const [picked, setPicked] = useState<readonly string[]>([])
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const toggle = useCallback((mailbox: string) => {
    setPicked((current) =>
      current.includes(mailbox) ? current.filter((one) => one !== mailbox) : [...current, mailbox],
    )
  }, [])

  /**
   * Clears every group, or only the ticked ones.
   *
   * `undefined` is "all", and it is not the same as sending every mailbox on
   * screen: all means whatever the server finds now, which is the honest
   * meaning of a button that says All.
   */
  const clear = useCallback(
    async (mailboxes: readonly string[] | undefined) => {
      setBusy(true)
      setNotice(undefined)
      setProblem(undefined)
      try {
        const { removed } = await client.clearDuplicates(
          mailboxes === undefined ? {} : { mailboxes: [...mailboxes] },
        )
        setPicked([])
        await reload()
        // Only when rows actually went. Zero changed nothing, and reloading the
        // board would suggest it had.
        if (removed > 0) onCleared()

        // Zero is not a failure: somebody else cleared them first, and the
        // reload above has already shown that.
        setNotice(
          removed === 0
            ? 'There was nothing left to clear.'
            : removed === 1
              ? 'Cleared. One duplicate was removed and what it held was kept.'
              : `Cleared. ${removed} duplicates were removed and what they held was kept.`,
        )
      } catch {
        setProblem('We could not clear those. Nothing was removed — try again.')
      } finally {
        setBusy(false)
      }
    },
    [client, onCleared, reload],
  )

  return { state, picked, toggle, notice, problem, busy, clear, retry: reload }
}
